<template>
    <div class="pan-and-zoom">
        <span v-if="placeholder">No Seating plan available yet...!</span>
        <div style="width: 100%; height: 100%;" v-else>
            <ul class="legend" v-if="variations">
                <li v-if="blockContext(variation)" v-for="variation in variations"><i :class="['fas fa-circle']" :style="{ color: variation.colour }"></i> {{ variation.name }} - <span v-html="currency.symbol"></span>{{ variation.price }}</li>
            </ul>
            <slot></slot>
            <div class="zoom-controls">
                <i @click="zoomOut()" class="fas fa-minus btn--quaternary zoom-out"></i>
                <span @click="reset()" class="btn--quaternary">{{ __('Reset') }}</span>
                <i @click="zoomIn()" class="fas fa-plus btn--quaternary zoom-in"></i>
            </div>
        </div>
    </div>
</template>

<script>
    import svgPanZoom from 'svg-pan-zoom';
    import Hammer from 'hammerjs';

    export default {
        props: {
            variations: {
                type: Array,
                required: false,
                default: () => {}
            },
            currentBlock: {
                required: true
            }
        },
        data: () => ({
            placeholder: false,
            panZoom: null,
            currency: window.currency,
            initInterval: null
        }),
        methods: {
            initPanZoom() {
                let eventsHandler = {
                    haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
                    init: function(options) {
                        var instance = options.instance,
                            initialScale = 1,
                            pannedX = 0,
                            pannedY = 0;

                        this.hammer = Hammer(options.svgElement, {
                            inputClass: Hammer.SUPPORT_POINTER_EVENTS ? Hammer.PointerEventInput : Hammer.TouchInput
                        });

                        this.hammer.get('pinch').set({enable: true});

                        this.hammer.on('doubletap', function(ev){
                            instance.zoomIn();
                        })

                        this.hammer.on('panstart panmove', function(ev){
                            if (ev.type === 'panstart') {
                                pannedX = 0;
                                pannedY = 0;
                            }

                            instance.panBy({x: ev.deltaX - pannedX, y: ev.deltaY - pannedY});
                            pannedX = ev.deltaX;
                            pannedY = ev.deltaY;
                        });

                        this.hammer.on('pinchstart pinchmove', function(ev){
                            if (ev.type === 'pinchstart') {
                                initialScale = instance.getZoom();
                                instance.zoomAtPoint(initialScale * ev.scale, {x: ev.center.x, y: ev.center.y});
                            }

                            instance.zoomAtPoint(initialScale * ev.scale, {x: ev.center.x, y: ev.center.y});
                        })

                        options.svgElement.addEventListener('touchmove', function(e){
                            e.preventDefault();
                        });
                    },
                    destroy: function(){
                        this.hammer.destroy();
                    }
                }
                this.panZoom = svgPanZoom('.svg-to-pan-and-zoom', {
                    zoomEnabled: true,
                    minZoom: 1,
                    maxZoom: 3,
                    controlIconsEnabled: false,
                    fit: 1,
                    center: 1,
                    beforePan: function(oldPan, newPan){
                        let stopHorizontal = false,
                            stopVertical = false,
                            gutterWidth = 200,
                            gutterHeight = 200,
                            // Computed variables
                            sizes = this.getSizes(),
                            leftLimit = -((sizes.viewBox.x + sizes.viewBox.width) * sizes.realZoom) + gutterWidth,
                            rightLimit = sizes.width - gutterWidth - (sizes.viewBox.x * sizes.realZoom),
                            topLimit = -((sizes.viewBox.y + sizes.viewBox.height) * sizes.realZoom) + gutterHeight,
                            bottomLimit = sizes.height - gutterHeight - (sizes.viewBox.y * sizes.realZoom);

                        let customPan = {};
                        customPan.x = Math.max(leftLimit, Math.min(rightLimit, newPan.x));
                        customPan.y = Math.max(topLimit, Math.min(bottomLimit, newPan.y));

                        return customPan;
                    },
                    customEventsHandler: eventsHandler
                });
            },
            zoomOut() {
                if (!this.panZoom) this.initPanZoom();
                this.panZoom.zoomOut();
            },
            reset() {
                if (!this.panZoom) this.initPanZoom();
                this.panZoom.reset();
            },
            zoomIn() {
                if (!this.panZoom) this.initPanZoom();
                this.panZoom.zoomIn();
            },
            resizeHandler() {
                if (!this.panZoom) this.initPanZoom();
                this.panZoom.resize();
                this.panZoom.fit();
                this.panZoom.center();
            },
            blockContext(variation) {
                var contextRelevant = false;
                if (variation.variables.length > 0) {
                    variation.variables.forEach((variable) => {
                        if (variable.block == this.currentBlock) {
                            contextRelevant = true;
                        }
                    });
                } else {
                    contextRelevant = true;
                }
                return contextRelevant;
            }
        },
        mounted() {
            var vm = this;
            if(!this.$slots.default) {
                this.placeholder = true;
                return false;
            }
            this.initInterval = setInterval(function() {
                if (!vm.panZoom) {
                    try {
                        vm.initPanZoom();
                    } catch(e) {}
                }
                if (vm.panZoom) {
                    clearInterval(vm.initInterval);
                }
            }, 100);
        },
        created() {
            window.addEventListener("resize", this.resizeHandler);
        },
        destroyed() {
            window.removeEventListener("resize", this.resizeHandler);
        }
    }
</script>

<style lang="scss" scoped>
    .pan-and-zoom {
        margin: 0 auto;
        width: 100%;
        & > div {
            height: 100%;
            width: 100%;
        }
    }
    .pan-and-zoom-container {
        width: 100%;
        height: calc(100% - 55px);
    }
    @media (max-width: 900px) {
        .pan-and-zoom {
            height: calc(80vh - 100px);
        }
    }
    @media (min-width: 901px) {
        .pan-and-zoom {
            height: 600px;
        }
        .pan-and-zoom-container {
            height: 100% !important;
        }
        .zoom-controls {
            position: absolute;
            right: 50px;
            top: 150px;
            .zoom-in {
                position: absolute;
                top: -35px;
                right: 15px;
            }
            .zoom-out {
                position: absolute;
                top: 36px;
                right: 15px;
            }
        }
    }
    .legend {
        text-align: left;
        position: absolute;
        left: 30px;
        top: 50px;
        background: rgba(255,255,255,.8);
        padding: 10px;
        margin-top: 10px;
        width: 200px;
        text-indent: -11px;
        li {
            line-height: 1.7em;
            i {
                margin-right: 5px;
            }
        }
    }
    .zoom-controls {
        margin-top: 10px;
        > * {
            cursor: pointer;
            padding: 5px 10px;
        }
    }
</style>



// WEBPACK FOOTER //
// resources/js/components/SVGPanAndZoom.vue